/* Tool steps and reasoning (rule 20, spec 6.3; AG-UI event names, MIT). Collapsed by default into one
   summary line ("Thought for 14 s · used 3 tools · 1 warning"); expands to a vertical timeline. */
import type { ReactNode } from 'react';
import {
  ChevronDown, FileText, Globe, Hand, PenLine, ShieldAlert, Sparkles, Terminal, Wrench, type LucideIcon,
} from 'lucide-react';
import type { StepStatus } from '../lib/api-types';
import { cx } from './cx';

export interface ToolStepSpec {
  id: string;
  label: string;
  target?: string;
  kind?: string;
  status: StepStatus;
  durationMs?: number;
  detail?: ReactNode;
  cap?: string;
}

const KIND_ICON: Record<string, LucideIcon> = {
  files: FileText, file: FileText, web: Globe, web_search: Globe, search: Globe, code: Terminal, run_code: Terminal,
  warning: ShieldAlert, approval: Hand, write: PenLine, tool: Wrench,
};
const STATUS_TEXT: Record<StepStatus, string> = {
  queued: 'Queued', running: 'Running', ok: 'Done', error: 'Failed', skipped: 'Skipped', waiting: 'Waiting for you', warning: 'Warning',
};

export const fmtDuration = (ms?: number) => (ms === undefined ? '' : ms < 1000 ? `${ms} ms` : `${(ms / 1000).toFixed(ms < 10_000 ? 1 : 0)} s`);

export function ToolStep({ step }: { step: ToolStepSpec }) {
  const Icon = KIND_ICON[step.kind ?? ''] ?? (step.status === 'warning' ? ShieldAlert : Wrench);
  return (
    <li data-status={step.status} data-cap={step.cap}>
      <Icon size={16} aria-hidden="true" />
      <div className="st-body">
        <b>{step.label}</b>{step.target && <> <span className="muted">{step.target}</span></>}
        <span className="sr-only">, {STATUS_TEXT[step.status]}</span>
        <div className="evt">{STATUS_TEXT[step.status]}{step.durationMs !== undefined ? ` · ${fmtDuration(step.durationMs)}` : ''}</div>
        {step.detail && <div className="st-detail">{step.detail}</div>}
      </div>
    </li>
  );
}

export interface ToolStepsProps {
  steps: ToolStepSpec[];
  reasoning?: string;
  /** Total thinking time, for the summary. */
  durationMs?: number;
  running?: boolean;
  defaultOpen?: boolean;
  className?: string;
}

export function stepsSummary(steps: ToolStepSpec[], durationMs?: number, running?: boolean): string {
  const tools = steps.filter((s) => s.kind !== 'warning' && s.status !== 'warning').length;
  const warnings = steps.filter((s) => s.status === 'warning').length;
  const waiting = steps.some((s) => s.status === 'waiting');
  const now = steps.find((s) => s.status === 'running');
  const parts: string[] = [];
  if (running && now) parts.push(`${now.label}…`);
  else if (durationMs !== undefined) parts.push(`Thought for ${Math.max(1, Math.round(durationMs / 1000))} s`);
  parts.push(`used ${tools} tool${tools === 1 ? '' : 's'}`);
  if (warnings) parts.push(`${warnings} warning${warnings === 1 ? '' : 's'}`);
  if (waiting) parts.push('waiting for you');
  const s = parts.join(' · ');
  return s[0]!.toUpperCase() + s.slice(1);
}

export function ToolSteps({ steps, reasoning, durationMs, running, defaultOpen, className }: ToolStepsProps) {
  if (!steps.length && !reasoning) return null;
  return (
    <details className={cx('stepgrp', className)} open={defaultOpen} data-cap="AGENT-01">
      <summary>
        <Sparkles size={16} aria-hidden="true" />
        <span>{stepsSummary(steps, durationMs, running)}</span>
        <ChevronDown size={16} className="chev" aria-hidden="true" />
      </summary>
      <ol className="tl">
        {steps.map((s) => <ToolStep key={s.id} step={s} />)}
      </ol>
      {reasoning && <p className="reason">Summary of reasoning: {reasoning}</p>}
    </details>
  );
}
